import { Modal, Spinner, useTheme, Theme, rgba } from '@salecycle/ui-component-library';
import { TFunction } from 'i18next';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import useNotifications from 'core/notifications/useNotifications';
import usePrevious from 'core/ui/utils/usePrevious';
import { useTemplate, postEmailTemplate } from '../../../data/CampaignsApi';
import { usePermissions } from 'core/app/AppContext';

const getScale = (width: number, emailWidth: number): number => {
  return width / emailWidth;
};

const getRemValue = (height: number) => {
  return `${height / 10}rem`;
};

const getEmailWidth = (emailWidth: number) => {
  return `${getRemValue(emailWidth)}`;
};

const getEmailHeight = (width: number, height: number, emailWidth: number) => {
  return `${getRemValue((height / width) * emailWidth)}`;
};

const Preview = styled.div<{ width: number; height: number; emailWidth: number }>`
  width: ${({ width }) => getRemValue(width)};
  height: ${({ height }) => getRemValue(height)};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
`;

const Content = styled.div<{ width: number; height: number; emailWidth: number }>`
  transform-origin: top left;
  transform: ${({ width, emailWidth }) => `scale(${getScale(width, emailWidth)})`};
  width: ${({ emailWidth }) => getEmailWidth(emailWidth)};
  height: ${({ height, width, emailWidth }) => getEmailHeight(width, height, emailWidth)};
`;

const Frame = styled.iframe<{ width: number; height: number; emailWidth: number; isFullPreview: boolean }>`
  border: none;
  pointer-events: ${({ isFullPreview }) => (isFullPreview ? null : 'none')};
  width: ${({ emailWidth }) => getEmailWidth(emailWidth)};
  height: ${({ height, width, emailWidth }) => getEmailHeight(width, height, emailWidth)};
  overflow: hidden;
`;

const PreviewButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  box-shadow: none;
  cursor: zoom-in;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const renderPreviewContent = (
  width: number,
  height: number,
  emailWidth: number,
  templateId: string,
  compiledHtml: string,
  status: string,
  scrolling: boolean,
  theme: Theme,
  t: TFunction,
) => {
  let srcDoc = compiledHtml;

  if (scrolling) {
    const headIndex = compiledHtml.indexOf('</head>');

    // If we allow scrolling and there's a </head> tag in the html we will add
    // some custom CSS to show a custom scrollbar on the iframe and also prevent
    // the user from clicking links inside the template
    if (headIndex !== -1) {
      const scrollbarHtml = `
      <style type="text/css">
        body::-webkit-scrollbar {
          background-color: transparent;
          height: 10px;
          width: 10px;
        }

        body::-webkit-scrollbar-thumb {
          background-color: ${rgba(theme.vars.newColor.black, 0.5)};
          border-radius: 0;
        }

        body::-webkit-scrollbar-track {
          background-color: ${rgba(theme.vars.newColor.black, 0.1)};
        }

        * {
          pointer-events: none;
        }
      </style>
    `;

      // Insert the scrollbar html before the closing </head> tag
      srcDoc = compiledHtml.slice(0, headIndex) + scrollbarHtml + compiledHtml.slice(headIndex);
    }
  }

  return (
    <Preview width={width} height={height} emailWidth={emailWidth} data-testid="TemplateEmailPreview">
      {status === 'pending' && (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      )}

      <Content width={width} height={height} emailWidth={emailWidth}>
        {status === 'resolved' && (
          <Frame
            title={t('campaigns:editor.rulesets.channels.template.emailPreview.title', { name: templateId })}
            srcDoc={srcDoc}
            tabIndex={!scrolling ? -1 : 0}
            width={width}
            height={height}
            emailWidth={emailWidth}
            scrolling={!scrolling ? 'no' : 'yes'}
            isFullPreview={scrolling}
            sandbox=""
          />
        )}
      </Content>
    </Preview>
  );
};

interface TemplateEmailPreviewProps {
  templateId: string;
  siteId: string;
  width: number;
  height: number;
  templateName: string;
}

export const TemplateEmailPreview: FC<TemplateEmailPreviewProps> = ({
  templateId,
  siteId,
  width,
  height,
  templateName,
}) => {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();

  const { showNotification } = useNotifications();
  const { hasPermission } = usePermissions();

  const enabled = hasPermission('ManageTemplates');

  const [compiledHtml, setCompiledHtml] = useState('');
  const [previewStatus, setPreviewStatus] = useState('idle');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { template, status: templateStatus } = useTemplate(enabled, siteId, templateId);
  const templateHtml = template?.template;
  const previousTemplateHtml = usePrevious(templateHtml);

  const emailWidth = 800;

  useEffect(() => {
    if (templateStatus === 'pending' || templateStatus === 'rejected') {
      setPreviewStatus(templateStatus);
    }
  }, [templateStatus]);

  useEffect(() => {
    let didCancel = false;

    const getEmailPreview = async () => {
      if (!didCancel) {
        setPreviewStatus('pending');

        await postEmailTemplate(templateHtml, siteId)
          .then(response => {
            setCompiledHtml(response?.entity?.compiled ?? '');
            setPreviewStatus('resolved');
          })
          .catch(() => {
            showNotification(
              t('campaigns:editor.rulesets.channels.template.emailPreview.error', { name: templateId }),
              'error',
            );

            setPreviewStatus('rejected');
          });
      }
    };

    if (enabled && templateStatus === 'resolved' && templateHtml !== previousTemplateHtml) {
      getEmailPreview();
    }

    return () => {
      didCancel = true;
    };
  }, [t, templateStatus, templateHtml, previousTemplateHtml, siteId, showNotification, templateId, enabled]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const hidePreview = !enabled || previewStatus === 'idle' || previewStatus === 'rejected';

  if (hidePreview) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onCloseButtonClicked={toggleModal}
        onOkButtonClicked={toggleModal}
        heading={templateName}
        variation="clean"
        overflow="visible"
        size={'wide'}
        okButtonLabel={t('campaigns:editor.rulesets.channels.template.emailPreview.modalOk')}
      >
        {renderPreviewContent(800, 450, emailWidth, templateId, compiledHtml, previewStatus, true, theme, t)}
      </Modal>

      {renderPreviewContent(width, height, emailWidth, templateId, compiledHtml, previewStatus, false, theme, t)}

      {previewStatus === 'resolved' && (
        <PreviewButton
          onClick={toggleModal}
          aria-label={t('campaigns:editor.rulesets.channels.template.emailPreview.openPreview')}
          data-testid="TemplateEmailPreviewButton"
        />
      )}
    </>
  );
};
